// Het vlak "Ketenbalans" (sessie 0b, 27-10; ADR 2026-10-27-ketenbalans-0b).
//
// De owner-regel: niets verdwijnt stil uit de keten. Per propositie één kaart
// met één zin ("Niets verdwijnt stil" of "Op 3 plekken verdwijnt iets zonder
// reden"), de poort (open, zou dicht gaan, dicht) en per stap van de keten een
// regel met een stoplicht. Wat niet in orde is staat er meteen in gewone taal;
// de rest klapt open. Voor iemand die niet data-savvy is: geen codes in de
// hoofdtekst (de code staat klein erachter, voor wie het wil narekenen).
//
// Alles komt van de server (sof/ketenbalans): dit scherm rekent niets na. Drie
// knoppen schrijven in de gedeelde database, elk alleen op een klik met een
// bevestiging: de poort afdwingen of terug naar alleen melden, een uitzondering
// goedkeuren (met verplichte reden) en een uitzondering intrekken.

import { useState } from "react";

import { Card } from "../../components/ui";
import { LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { KetenbalansKaart, KetenbalansMeter, KetenbalansStap } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { Stoplicht } from "./Stoplicht";
import { invoer, knopDonker, knopLicht } from "./stijl";

const POORT_KLEUR: Record<string, string> = {
  open: color.accentGreen,
  "zou dicht gaan": color.accentAmber,
  dicht: color.accentRed,
  "niet gepoort": color.faint,
};

function tijd(op: string | null | undefined): string {
  if (!op) return "nog nooit";
  return new Date(op).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Ketenbalans() {
  const res = useResource(() => api.ketenbalans(), []);
  if (res.error) return <LoadError reason={res.error} onRetry={res.reload} />;
  if (!res.data) return <p style={{ color: color.faint, fontSize: size.meta }}>Laden…</p>;
  const d = res.data;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
      <Card style={{ padding: 20 }}>
        <h2 style={{ margin: 0, fontSize: size.panelTitle, color: color.ink }}>
          Verdwijnt er iets stil uit de keten?
        </h2>
        <p style={{ margin: "6px 0 0", fontSize: size.body, color: color.body }}>
          Elke bron, signaal en skill hoort een stap te verlaten met een reden: door, afgewezen,
          fout of wacht. Hieronder per propositie waar dat niet zo is.
        </p>
        <Schakelaar
          afdwingen={d.afdwingen}
          zin={d.zin}
          gezet={d.schakelaar ? `${d.schakelaar.gezetDoor}, ${tijd(d.schakelaar.gezetOp)}` : null}
          herlaad={res.reload}
        />
      </Card>
      {d.proposities.map((k) => (
        <KaartVanPropositie key={k.sector} kaart={k} herlaad={res.reload} />
      ))}
    </div>
  );
}

function Schakelaar({
  afdwingen,
  zin,
  gezet,
  herlaad,
}: {
  afdwingen: boolean;
  zin: string;
  gezet: string | null;
  herlaad: () => void;
}) {
  const [vraag, setVraag] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const zet = async () => {
    setBezig(true);
    setFout(null);
    try {
      await api.ketenbalansSchakelaar({ afdwingen: !afdwingen, toelichting: null });
      setVraag(false);
      herlaad();
    } catch (e) {
      setFout(e instanceof Error ? e.message : "Niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  return (
    <div
      data-poortstand={afdwingen ? "afdwingen" : "melden"}
      style={{
        marginTop: 14,
        padding: "12px 14px",
        borderRadius: radius.block,
        background: afdwingen ? color.accentAmberBg : "transparent",
        border: `1px solid ${color.border}`,
        display: "grid",
        gap: 8,
      }}
    >
      <div style={{ fontSize: size.body, color: color.ink }}>{zin}</div>
      {gezet && <div style={{ fontSize: size.meta, color: color.faint }}>Laatst gezet door {gezet}.</div>}
      {!vraag ? (
        <div>
          <button type="button" style={knopLicht} onClick={() => setVraag(true)}>
            {afdwingen ? "Terug naar alleen melden" : "De poort laten tegenhouden"}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: size.meta, color: color.body }}>
            {afdwingen
              ? "Dan verdwijnt er niets meer van het scherm, ook niet bij rode meters."
              : "Dan ziet alleen beheer een propositie waar iets stil verdwijnt; de anderen niet meer."}
          </span>
          <button type="button" style={knopDonker(bezig)} disabled={bezig} onClick={() => void zet()}>
            Bevestig
          </button>
          <button type="button" style={knopLicht} onClick={() => setVraag(false)}>
            Annuleer
          </button>
        </div>
      )}
      {fout && (
        <span role="alert" style={{ fontSize: size.meta, color: color.accentRed }}>
          {fout}
        </span>
      )}
    </div>
  );
}

function KaartVanPropositie({ kaart, herlaad }: { kaart: KetenbalansKaart; herlaad: () => void }) {
  const [alles, setAlles] = useState(false);
  return (
    <Card style={{ padding: 20 }}>
      <div data-propositie={kaart.sector} style={{ display: "grid", gap: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: size.panelTitle, color: color.ink }}>{kaart.naam}</h3>
          <span
            data-poort={kaart.poort}
            style={{
              fontSize: size.meta,
              fontWeight: weight.medium,
              color: POORT_KLEUR[kaart.poort] ?? color.body,
              border: `1px solid ${color.border}`,
              borderRadius: radius.pill,
              padding: "2px 10px",
            }}
          >
            poort: {kaart.poort}
          </span>
        </div>
        <div style={{ fontSize: size.body, color: color.body }}>{kaart.zin}</div>
        <div style={{ fontSize: size.meta, color: color.faint }}>Gemeten: {tijd(kaart.gemetenOp)}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 6, marginTop: 14 }}>
        {kaart.stappen.map((s) => (
          <StapRij key={s.stap} stap={s} sector={kaart.sector} alles={alles} herlaad={herlaad} />
        ))}
      </div>
      {kaart.stappen.length > 0 && (
        <button type="button" style={{ ...knopLicht, marginTop: 10 }} onClick={() => setAlles((x) => !x)}>
          {alles ? "Alleen wat aandacht vraagt" : "Alle controles tonen"}
        </button>
      )}
    </Card>
  );
}

function StapRij({
  stap,
  sector,
  alles,
  herlaad,
}: {
  stap: KetenbalansStap;
  sector: string;
  alles: boolean;
  herlaad: () => void;
}) {
  const zichtbaar = alles ? stap.meters : stap.meters.filter((m) => m.stand !== "goed");
  return (
    <div
      data-stap={stap.stap}
      style={{
        border: `1px solid ${color.border}`,
        borderRadius: radius.block,
        padding: "8px 12px",
        display: "grid",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Stoplicht stand={stap.stand} />
        <span style={{ fontSize: size.body, color: color.ink, fontWeight: weight.medium }}>{stap.naam}</span>
        {stap.stand === "goed" && !alles && (
          <span style={{ fontSize: size.meta, color: color.faint }}>niets verdwijnt stil</span>
        )}
      </div>
      {zichtbaar.map((m) => (
        <MeterRegel key={m.code} meter={m} sector={sector} herlaad={herlaad} />
      ))}
    </div>
  );
}

function MeterRegel({
  meter,
  sector,
  herlaad,
}: {
  meter: KetenbalansMeter;
  sector: string;
  herlaad: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reden, setReden] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const doe = async (werk: () => Promise<unknown>) => {
    setBezig(true);
    setFout(null);
    try {
      await werk();
      setOpen(false);
      setReden("");
      herlaad();
    } catch (e) {
      setFout(e instanceof Error ? e.message : "Niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  const u = meter.uitzondering;
  const magToelaten = meter.moet0 && meter.stand === "rood" && !u;
  const redenen = meter.redenen.filter((r) => r.status !== "door");
  return (
    <div data-meter={meter.code} style={{ paddingLeft: 18, display: "grid", gap: 4 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <Stoplicht stand={meter.stand} reden={meter.reden} />
        <span style={{ fontSize: size.body, color: color.body, flex: "1 1 240px", minWidth: 0 }}>
          {meter.zin}
          {meter.noemer != null && meter.waarde != null && (
            <span style={{ color: color.faint }}> (van {meter.noemer.toLocaleString("nl-NL")})</span>
          )}
        </span>
        <span style={{ fontSize: size.label, color: color.faint }}>
          {meter.code} · {meter.lat}
        </span>
      </div>
      {redenen.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 34, display: "grid", gap: 1 }}>
          {redenen.map((r) => (
            <li key={`${r.status}-${r.reden}`} style={{ fontSize: size.meta, color: color.soft }}>
              {r.aantal.toLocaleString("nl-NL")} × {r.status}: {r.tekst}
            </li>
          ))}
        </ul>
      )}
      {u && (
        <div style={{ fontSize: size.meta, color: color.body, paddingLeft: 18 }}>
          Bewust toegestaan tot {u.totWaarde.toLocaleString("nl-NL")} door {u.goedgekeurdDoor}: “{u.reden}”{" "}
          <button
            type="button"
            style={{ ...knopLicht, padding: "2px 8px" }}
            disabled={bezig}
            onClick={() => void doe(() => api.ketenbalansIntrekken(u.id))}
          >
            Intrekken
          </button>
        </div>
      )}
      {magToelaten && !open && (
        <div style={{ paddingLeft: 18 }}>
          <button type="button" style={{ ...knopLicht, padding: "4px 10px" }} onClick={() => setOpen(true)}>
            Uitzondering goedkeuren
          </button>
        </div>
      )}
      {magToelaten && open && (
        <div style={{ paddingLeft: 18, display: "grid", gap: 6, maxWidth: 520 }}>
          <label style={{ fontSize: size.meta, color: color.body }}>
            Waarom mag dit (tijdelijk)? Geldt tot de waarde van nu; groeit het, dan houdt de poort weer tegen.
            <textarea
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              rows={2}
              style={{ ...invoer, marginTop: 4, resize: "vertical" }}
            />
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              style={knopDonker(bezig || reden.trim().length < 5)}
              disabled={bezig || reden.trim().length < 5}
              onClick={() => void doe(() => api.ketenbalansUitzondering({ sector, meter: meter.code, reden }))}
            >
              Goedkeuren
            </button>
            <button type="button" style={knopLicht} onClick={() => setOpen(false)}>
              Annuleer
            </button>
          </div>
        </div>
      )}
      {fout && (
        <span role="alert" style={{ fontSize: size.meta, color: color.accentRed, paddingLeft: 18 }}>
          {fout}
        </span>
      )}
    </div>
  );
}
